import React, {useState} from "react";
import {
  Input,
  Button,
  Caption1,
  Text,
  Tooltip,
  makeStyles,
  tokens,
} from "@fluentui/react-components";
import {
  Settings24Regular,
  ArrowLeft20Regular,
  PlugDisconnected24Regular,
  LockClosed24Regular,
  Info16Regular,
} from "@fluentui/react-icons";
import {useAppState} from "../context/AppContext";
import {MIN_ITERATIONS, MAX_ITERATIONS_LIMIT} from "../../services/chatTurn";
import {getManifestVersion} from "../../services/manifestVersion";
import {getRuntimeConfig} from "../../services/runtimeConfig";

/** Replaced at build time by webpack's DefinePlugin, from package.json. */
declare const __ADDIN_VERSION__: string;
const ADDIN_VERSION =
  typeof __ADDIN_VERSION__ === "string" ? __ADDIN_VERSION__ : "dev";

/**
 * The manifest version Office actually used to open this task pane — can lag
 * ADDIN_VERSION by up to ~72h while a new manifest.xml propagates (see
 * ../../services/manifestVersion.ts). Shown alongside the deployment version
 * so a mismatch is visible directly in the UI when debugging user reports.
 */
const MANIFEST_VERSION = getManifestVersion();

/** Hostname only (matches Outlook's Settings "LibreChat instance" row). */
function getInstanceHost(): string {
  const url =
    getRuntimeConfig().LIBRECHAT_API_URL || process.env.LIBRECHAT_API_URL || "";
  if (!url) return "Not configured";
  try {
    return new URL(url).host || url;
  } catch {
    return url;
  }
}

const useStyles = makeStyles({
  container: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
    padding: "16px",
    height: "100%",
    boxSizing: "border-box",
    overflowY: "auto",
  },
  header: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    fontSize: "18px",
    fontWeight: 600,
    color: tokens.colorNeutralForeground1,
  },
  card: {
    backgroundColor: tokens.colorNeutralBackground2,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: "10px",
    overflow: "hidden",
  },
  cardHeader: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    padding: "10px 16px",
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    backgroundColor: tokens.colorNeutralBackground1,
    fontSize: "13px",
    fontWeight: 600,
    color: tokens.colorNeutralForeground1,
  },
  cardBody: {
    padding: "16px",
  },
  identity: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
  },
  avatar: {
    width: "38px",
    height: "38px",
    flexShrink: 0,
    display: "grid",
    placeItems: "center",
    borderRadius: tokens.borderRadiusCircular,
    backgroundColor: tokens.colorBrandBackground2,
    color: tokens.colorBrandForeground2,
    fontSize: "16px",
    fontWeight: 600,
  },
  identityText: {
    display: "flex",
    flexDirection: "column",
    minWidth: 0,
  },
  email: {
    fontSize: "13px",
    fontWeight: 600,
    overflowWrap: "anywhere",
  },
  status: {
    fontSize: "12px",
    color: tokens.colorPaletteGreenForeground1,
  },
  detailRow: {
    display: "flex",
    alignItems: "baseline",
    justifyContent: "space-between",
    gap: "12px",
    padding: "8px 0",
    fontSize: "12px",
    "&:not(:first-child)": {
      borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    },
  },
  detailLabel: {
    color: tokens.colorNeutralForeground3,
  },
  detailValue: {
    maxWidth: "58%",
    textAlign: "right",
    overflowWrap: "anywhere",
    fontWeight: 600,
  },
  hint: {
    fontSize: "12px",
    color: tokens.colorNeutralForeground3,
  },
  version: {
    marginTop: "auto",
    paddingTop: "16px",
    textAlign: "center",
    color: tokens.colorNeutralForeground4,
  },
  advancedRow: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  advancedLabel: {
    display: "flex",
    alignItems: "center",
    gap: "4px",
    flexShrink: 0,
    color: tokens.colorNeutralForeground1,
    fontWeight: 600,
  },
  advancedInput: {
    flex: 1,
    minWidth: 0,
  },
  infoIcon: {
    color: tokens.colorNeutralForeground3,
    cursor: "help",
  },
  signOutButton: {
    color: tokens.colorPaletteRedForeground1,
    border: `1px solid ${tokens.colorPaletteRedBorder1}`,
    "&:hover": {
      color: tokens.colorPaletteRedForeground1,
      border: `1px solid ${tokens.colorPaletteRedBorder1}`,
      backgroundColor: tokens.colorPaletteRedBackground1,
    },
  },
});

export function Settings() {
  const styles = useStyles();
  const {
    apiKey,
    authStatus,
    signedInUser,
    maxIterations,
    setMaxIterations,
    setView,
    signIn,
    signOut,
  } = useAppState();

  const [isSigningOut, setIsSigningOut] = useState(false);

  async function handleSignOut() {
    setIsSigningOut(true);
    try {
      await signOut();
    } finally {
      setIsSigningOut(false);
    }
  }

  function handleGoToChat() {
    setView("chat");
  }

  if (authStatus === "unsupported") {
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <Settings24Regular />
          Settings
        </div>
        <div className={styles.card}>
          <div className={styles.cardHeader}>Office host not supported</div>
          <div className={styles.cardBody}>
            <Text>
              This Excel host does not support Nested App Authentication. The
              add-in needs NAA support to sign in securely.
            </Text>
          </div>
        </div>
        <Caption1 className={styles.version}>
          NOS-GPT Excel v{ADDIN_VERSION} ·{" "}
          {MANIFEST_VERSION === "unknown"
            ? "Manifest unknown"
            : `Manifest v${MANIFEST_VERSION}`}
        </Caption1>
      </div>
    );
  }

  if (authStatus === "sign-in-required") {
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <Settings24Regular />
          Settings
        </div>
        <div className={styles.card}>
          <div className={styles.cardHeader}>Sign in required</div>
          <div className={styles.cardBody}>
            <Text>
              The add-in needs to sign you into LibreChat before chat or
              worksheet actions can run.
            </Text>
          </div>
        </div>
        <Button appearance="primary" onClick={() => void signIn()}>
          Sign in with Microsoft
        </Button>
        <Caption1 className={styles.version}>
          NOS-GPT Excel v{ADDIN_VERSION} ·{" "}
          {MANIFEST_VERSION === "unknown"
            ? "Manifest unknown"
            : `Manifest v${MANIFEST_VERSION}`}
        </Caption1>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        {apiKey && (
          <Button
            appearance="subtle"
            size="small"
            icon={<ArrowLeft20Regular />}
            onClick={handleGoToChat}
            aria-label="Back to chat"
          />
        )}
        <Settings24Regular />
        Settings
      </div>

      <div className={styles.card}>
        <div className={styles.cardHeader}>Account</div>
        <div className={styles.cardBody}>
          <div className={styles.identity}>
            <div className={styles.avatar} aria-hidden="true">
              {(signedInUser || "?").charAt(0).toUpperCase()}
            </div>
            <div className={styles.identityText}>
              <Text className={styles.email}>
                {signedInUser || "Signed in"}
              </Text>
              <Text className={styles.status}>Connected to LibreChat</Text>
            </div>
          </div>
        </div>
      </div>

      <div className={styles.card}>
        <div className={styles.cardHeader}>
          <LockClosed24Regular fontSize={16} />
          Connection
        </div>
        <div className={styles.cardBody}>
          <div className={styles.detailRow}>
            <Text className={styles.detailLabel}>LibreChat instance</Text>
            <Text className={styles.detailValue}>{getInstanceHost()}</Text>
          </div>
          <div className={styles.detailRow}>
            <Text className={styles.detailLabel}>Deployment version</Text>
            <Text className={styles.detailValue}>{ADDIN_VERSION}</Text>
          </div>
          <div className={styles.detailRow}>
            <Text className={styles.detailLabel}>Manifest version</Text>
            <Text className={styles.detailValue}>
              {MANIFEST_VERSION === "unknown" ? "unknown" : MANIFEST_VERSION}
            </Text>
          </div>
        </div>
      </div>

      <div className={styles.card}>
        <div className={styles.cardHeader}>Advanced</div>
        <div className={styles.cardBody}>
          <div className={styles.advancedRow}>
            <span className={styles.advancedLabel}>
              Max iterations
              <Tooltip
                relationship="label"
                content={`How many back-and-forth steps the assistant can take per request (1–${MAX_ITERATIONS_LIMIT}). Higher values mean slower, costlier turns — each step re-sends the full workbook.`}
              >
                <Info16Regular className={styles.infoIcon} tabIndex={0} />
              </Tooltip>
            </span>
            <Input
              id="max-iterations-input"
              className={styles.advancedInput}
              type="number"
              min={MIN_ITERATIONS}
              max={MAX_ITERATIONS_LIMIT}
              value={String(maxIterations)}
              onChange={(_, data) => {
                const parsed = Number(data.value);
                if (Number.isFinite(parsed)) setMaxIterations(parsed);
              }}
            />
          </div>
        </div>
      </div>

      <Button
        appearance="outline"
        className={styles.signOutButton}
        onClick={handleSignOut}
        disabled={isSigningOut || !apiKey}
        icon={<PlugDisconnected24Regular />}
      >
        {isSigningOut ? "Signing out..." : "Sign out"}
      </Button>
    </div>
  );
}
